import * as Yup from 'yup';
import type { IncidentReportYesNoOther } from 'components/CriticalIncident/interfaces';
import { yesNoOther, requiredWhenIs, requiredWhenValueIn, minOneWhenTrue } from '../utils';
import { riskAssessmentCoreSchema } from '../RiskAssessments/schema';

const requiredWhenSupervisionIs = (allowedValues: IncidentReportYesNoOther[]) => ({
    is: (atRisk: boolean, supervision: IncidentReportYesNoOther) => atRisk && allowedValues.includes(supervision),
    then: (schema: Yup.StringSchema<string | undefined, Yup.AnyObject, undefined, "">) => schema.required('error_required_field'),
    otherwise: (schema: Yup.StringSchema<string | undefined, Yup.AnyObject, undefined, "">) => schema.notRequired(),
});

const participantSchema = Yup.object({
    name: Yup.string().max(200).when('declinedToProvideInformation', requiredWhenIs(false)),
    dateOfBirth: Yup.string().when('declinedToProvideInformation', requiredWhenIs(false)),
    wasNatureOfSupportAndConfidentialityDiscussed: Yup.boolean().required('error_required_field'),
    howIndividualPresented: Yup.string().max(2000),
    interventionProvided: Yup.string().max(2000).required('error_required_field'),
    followUpNotes: Yup.string().max(2000),
    declinedToProvideInformation: Yup.boolean().required('error_required_field'),
    wasTheIndividualAtRisk: Yup.boolean().required('error_required_field'),
    wasThereSupervisionOrConsultation: yesNoOther.when('wasTheIndividualAtRisk', requiredWhenIs(true)),
    wasThereSupervisionOrConsultationOtherReason: Yup.string().max(2000).when(['wasTheIndividualAtRisk', 'wasThereSupervisionOrConsultation'], requiredWhenSupervisionIs(['Y', 'O'])),
    supervisionDate: Yup.string().when(['wasTheIndividualAtRisk', 'wasThereSupervisionOrConsultation'], requiredWhenSupervisionIs(['Y'])),
    supervisionOrConsultationOutcome: Yup.string().max(2000).when(['wasTheIndividualAtRisk', 'wasThereSupervisionOrConsultation'], requiredWhenSupervisionIs(['Y'])),
});

export const initialValues = {
    wereThereIndividualSupportSessions: null,
    noOfParticipants: 0,
    participants: [],
};

export const validationSchema = Yup.object({
    wereThereIndividualSupportSessions: Yup.boolean().required('error_required_field'),
    noOfParticipants: Yup.number().min(0).required('error_required_field'),
    participants: Yup.array().of(participantSchema),
});

// ---- AU (TPH-1906/1907/1908/1909) ----
// Best-effort draft, not yet confirmed against a real /v2 contract.

// Reuses riskAssessmentCoreSchema (risk factors, duty-to-report, notified parties) from the
// standalone RiskAssessments module — this nested object is only ever validated when
// wasTheIndividualAtRisk is true (see auParticipantSchema below), so its own fields don't need
// to re-check that flag.
const auRiskAssessmentSchema = riskAssessmentCoreSchema.shape({
    wasThereSupervisionOrConsultation: yesNoOther.required('error_required_field'),
    wasThereSupervisionOrConsultationOtherReason: Yup.string().max(2000).when('wasThereSupervisionOrConsultation', requiredWhenValueIn(['Y', 'O'], false)),
    supervisionDate: Yup.string().when('wasThereSupervisionOrConsultation', requiredWhenValueIn(['Y'])),
    supervisionOrConsultationOutcome: Yup.string().max(2000).when('wasThereSupervisionOrConsultation', requiredWhenValueIn(['Y'], false)),
});

const followUpSessionSchema = Yup.object({
    sessionDate: Yup.string().nullable().required('error_required_field'),
    sessionType: Yup.string().required('error_required_field'),
    presentation: Yup.string().max(2000).required('error_required_field'),
    risk: Yup.string().max(2000).required('error_required_field'),
    intervention: Yup.string().max(2000).required('error_required_field'),
    recommendation: Yup.string().max(2000).required('error_required_field'),
});

const auParticipantSchema = Yup.object({
    name: Yup.string().max(200).when('declinedToProvideInformation', requiredWhenIs(false)),
    dateOfBirth: Yup.string().when('declinedToProvideInformation', requiredWhenIs(false)),
    emailAddress: Yup.string().max(100).optional(),
    phoneNumber: Yup.string().max(100).optional(),
    wasNatureOfSupportAndConfidentialityDiscussed: Yup.boolean().required('error_required_field'),
    howIndividualPresented: Yup.string().max(2000),
    interventionProvided: Yup.string().max(2000).required('error_required_field'),
    followUpNotes: Yup.string().max(2000),
    declinedToProvideInformation: Yup.boolean().required('error_required_field'),
    wasTheIndividualAtRisk: Yup.boolean().required('error_required_field'),
    riskAssessment: Yup.mixed().when('wasTheIndividualAtRisk', {
        is: true,
        then: () => auRiskAssessmentSchema.required('error_required_field'),
        otherwise: (schema: Yup.MixedSchema) => schema.nullable().notRequired(),
    }),
    wereThereAnyFollowUpSessions: Yup.boolean().nullable(),
    noOfFollowUpSessions: Yup.number().min(0),
    followUpSessions: Yup.array().of(followUpSessionSchema).when('wereThereAnyFollowUpSessions', minOneWhenTrue()),
});

export const auValidationSchema = Yup.object({
    wereThereIndividualSupportSessions: Yup.boolean().required('error_required_field'),
    noOfParticipants: Yup.number().min(0).required('error_required_field'),
    participants: Yup.array().of(auParticipantSchema),
});
